import Spinner from '../Shared/Spinner';
import { FormStatus, SettingsState, Team } from '../../typedefs/store';
import Button from '../Shared/Button';
import { baseFormStyles, styledWithProps } from '../../util/styles';
import { Flex, TranscriptWrapper, WindowWrapper } from '../Conversation/Proactive/Styles';
import IconBar from '../Conversation/Proactive/IconBar';
import colors from '../colors';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { LeaveMessageFormPayload } from '../../typedefs/actions';
import * as CheckIcon from 'react-icons/lib/io/checkmark-circled';
import * as AlertIcon from 'react-icons/lib/io/alert-circled';

interface Props {
    phone: string | void;
    teams: Team[];
    removeConversation: (hide: boolean) => void;
    submit: (data: LeaveMessageFormPayload) => void;
    formStatus: FormStatus;
    settings: SettingsState;
}

interface State {
    teamId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
    errors: { [key: string]: string | boolean };
    url: string;
}

type inputChangeHandler = (e: React.KeyboardEvent<any>) => void;

export default class LeaveMessage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            teamId: props.teams[0].id,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: '',
            errors: {},
            url: window.location.href,
        };
    }

    hide = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.removeConversation(true);
    };

    handleTeam: inputChangeHandler = e => {
        this.setState({ teamId: Number(e.currentTarget.value) });
    };

    handleFirstName: inputChangeHandler = e => {
        this.setState({ firstName: e.currentTarget.value });
    };

    handleLastName: inputChangeHandler = e => {
        this.setState({ lastName: e.currentTarget.value });
    };

    handleEmail: inputChangeHandler = e => {
        this.setState({ email: e.currentTarget.value });
    };

    handlePhone: inputChangeHandler = e => {
        this.setState({ phone: e.currentTarget.value });
    };

    handleMessage: inputChangeHandler = e => {
        this.setState({ message: e.currentTarget.value });
    };

    handleSubmit = () => {
        const errors = this.validate();
        if (errors.hasError) {
            this.setState({ errors });
            return;
        }

        this.props.submit(this.state);
        this.setState({ errors: {} });
    };

    validate = () => {
        const errors: any = { hasError: false };
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (this.state.firstName.trim() === '') {
            errors.hasError = true;
            errors.firstName = 'Please enter your first name';
        }
        if (this.state.lastName.trim() === '') {
            errors.hasError = true;
            errors.lastName = 'Please enter your last name';
        }
        if (this.state.email.trim() === '' && this.state.phone.trim() === '') {
            errors.hasError = true;
            errors.email = 'Please enter your email or phone';
        }
        if (this.state.email.trim() !== '' && !emailRegex.test(this.state.email.trim())) {
            errors.hasError = true;
            errors.email = 'Please enter a valid email address';
        }
        if (this.state.phone.trim() !== '' && this.state.phone.trim().length < 7) {
            errors.hasError = true;
            errors.phone = 'Please enter a valid phone number';
        }
        if (this.state.message.trim() === '') {
            errors.hasError = true;
            errors.message = 'Please enter your message';
        }
        return errors;
    };

    render() {
        return (
            <WindowWrapper
                className="conversations-leave-msg"
                zIndex={this.props.settings.zIndex}
            >
                <IconBar onClose={this.hide} />
                <Flex>
                    <TranscriptWrapper>
                        {this.props.formStatus.submitting && (
                            <Submitting>
                                <Spinner size={36} />Sending
                            </Submitting>
                        )}

                        <H1>Leave Us a Message</H1>
                        {this.props.formStatus.submitError && (
                            <SubmitError>
                                <AlertIcon /> We're really sorry, but there was an error sending
                                your message. Please try again.
                            </SubmitError>
                        )}
                        {this.props.formStatus.submitSuccess && (
                            <SubmitSuccess>
                                <CheckIcon size={48} />
                                <div>Your message has been sent!</div>
                            </SubmitSuccess>
                        )}
                        <p>
                            We’re not able to chat right now, but we’ll email or call as soon as
                            possible.
                        </p>
                        {this.props.phone && <p>You can also call us at {this.props.phone}.</p>}
                        <Form>
                            <FormRow>
                                <FormItem hasError={false}>
                                    <Label htmlFor="teamId">Department</Label>
                                    <Select
                                        name="teamId"
                                        id="teamId"
                                        onChange={this.handleTeam}
                                        value={this.state.teamId}
                                    >
                                        {this.props.teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormItem>
                            </FormRow>
                            <FormRow>
                                <FormItem hasError={!!this.state.errors.firstName}>
                                    <Label htmlFor="firstName">First Name</Label>
                                    <Input
                                        name="firstName"
                                        id="firstName"
                                        onChange={this.handleFirstName}
                                        value={this.state.firstName}
                                    />
                                    {!!this.state.errors.firstName && (
                                        <ErrorMessage>{this.state.errors.firstName}</ErrorMessage>
                                    )}
                                </FormItem>
                                <FormItem hasError={!!this.state.errors.lastName}>
                                    <Label htmlFor="lastName">Last Name</Label>
                                    <Input
                                        name="lastName"
                                        id="lastName"
                                        onChange={this.handleLastName}
                                        value={this.state.lastName}
                                    />
                                    {!!this.state.errors.lastName && (
                                        <ErrorMessage>{this.state.errors.lastName}</ErrorMessage>
                                    )}
                                </FormItem>
                            </FormRow>
                            <FormRow>
                                <FormItem hasError={!!this.state.errors.email}>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        name="email"
                                        id="email"
                                        onChange={this.handleEmail}
                                        value={this.state.email}
                                    />
                                    {!!this.state.errors.email && (
                                        <ErrorMessage>{this.state.errors.email}</ErrorMessage>
                                    )}
                                </FormItem>
                            </FormRow>
                            <FormRow>
                                <FormItem hasError={!!this.state.errors.phone}>
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input
                                        name="phone"
                                        id="phone"
                                        onChange={this.handlePhone}
                                        value={this.state.phone}
                                    />
                                    {!!this.state.errors.phone && (
                                        <ErrorMessage>{this.state.errors.phone}</ErrorMessage>
                                    )}
                                </FormItem>
                            </FormRow>
                            <FormRow>
                                <FormItem hasError={!!this.state.errors.message}>
                                    <Label htmlFor="message">Message</Label>
                                    <Textarea
                                        rows={8}
                                        name="message"
                                        id="message"
                                        onChange={this.handleMessage}
                                        value={this.state.message}
                                    />
                                    {!!this.state.errors.message && (
                                        <ErrorMessage>{this.state.errors.message}</ErrorMessage>
                                    )}
                                </FormItem>
                            </FormRow>
                            <ButtonWrapper>
                                <Button type="submit" primary={true} onClick={this.handleSubmit}>
                                    Send Message
                                </Button>
                            </ButtonWrapper>
                        </Form>
                    </TranscriptWrapper>
                </Flex>
            </WindowWrapper>
        );
    }
}

const H1 = styled.h1`
    text-align: center;
    margin-bottom: 0.5em !important;
`;

const Label = styled.label`
    font-size: 12px;
    color: ${colors.mediumDarkGray};
`;

const ErrorMessage = styled.div`
    margin-top: 3px;
    color: ${colors.red};
    font-size: 10px;
    line-height: 1;
`;

const Form = styled.form`margin-top: 1em;`;

const FormRow = styled.div`
    margin-bottom: 0.5em;
    display: flex;
`;

const FormItem = styledWithProps<{ hasError?: boolean }>(styled.div)`
    flex-grow: 1;
    flex-basis: 50%;
    margin-right: 1em;
    &:last-child {
        margin-right: 0;
    }
    ${(props: any) =>
        props.hasError &&
        css`
            label {
                color: ${colors.red};
            }
            input,
            select,
            textarea {
                border-color: ${colors.red};
            }
        `};
`;

const ButtonWrapper = styled(FormRow)`
    justify-content: flex-end;
    margin: 1em 0 0 0;
`;

const Input = styled.input`
    ${baseFormStyles} box-sizing: border-box;
    width: 100%;
    padding: 6px;
    border-radius: 3px;
`;

const Textarea = styled.textarea`
    ${baseFormStyles} box-sizing: border-box;
    width: 100%;
    padding: 6px;
    border-radius: 3px;
`;

const Select = styled.select`
    ${baseFormStyles} box-sizing: border-box;
    width: 100%;
    padding: 6px;
    border-radius: 3px;
    appearance: menulist;
    height: 31px;
`;

const Submitting = styled.div`
    padding: 1em;
    background-color: rgba(255, 255, 255, 0.9);
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 1.5em;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

const SubmitError = styled.div`
    padding: 1em;
    background-color: ${colors.red};
    color: white;
`;

const SubmitSuccess = styled.div`
    padding: 1em;
    background-color: rgba(255, 255, 255, 0.9);
    color: ${props => props.theme.accent1};
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 1.5em;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/LeaveMessage/LeaveMessage.tsx